import Vector3 from '../math/Vector3.js';
import Transform from '../math/Transform.js';
import DirectionResolver from './DirectionResolver.js';

/**
 * CharacterController - Handles player input and character physics
 *
 * Responsibilities:
 * - Convert WASD input to world-space movement direction
 * - Apply movement velocity based on input
 * - Check collisions before moving
 * - Detect ground beneath character
 * - Handle gravity when not grounded
 * - Trigger animation state changes
 */
export default class CharacterController {
  // Movement speeds
  static WALK_SPEED = 5; // Units per second
  static RUN_SPEED = 10; // Units per second

  // Physics
  static GRAVITY = 20; // Units per second squared
  static JUMP_VELOCITY = 10; // Units per second (max height = v²/2g = 2.5 units)

  // Maximum fall speed to prevent tunneling
  static MAX_FALL_SPEED = 50;

  /**
   * Create a new character controller
   * @param {Character} character - The character to control
   * @param {Object} options - Controller options
   * @param {InputManager} options.inputManager - Input manager for reading keys
   * @param {Camera} options.camera - Camera for direction calculation
   * @param {CharacterCollisionSystem} options.collisionSystem - Collision system
   * @param {GroundDetector} options.groundDetector - Ground detection
   */
  constructor(character, options = {}) {
    this.character = character;
    this.inputManager = options.inputManager;
    this.camera = options.camera;
    this.collisionSystem = options.collisionSystem;
    this.groundDetector = options.groundDetector;
    this.inventory = options.inventory || null;

    // Input state
    this.inputDirection = new Vector3(0, 0, 0);
    this.isRunning = false;
    this.jumpPressed = false;  // Track if jump key was pressed this frame
    this.jumpHeld = false;     // Track if jump key is being held (to prevent repeated jumps)
    this.wasGrounded = true;   // Track previous grounded state for landing detection
    this._fallStartY = null;   // Y position when character became airborne

    // Attack input state (E, R, F keys)
    this.attackPressed = null;  // Which attack was pressed this frame (or null)
    this.attackHeld = { e: false, r: false, f: false };  // Track held state for each key

    // Movement speed configuration
    this.walkSpeed = options.walkSpeed || CharacterController.WALK_SPEED;
    this.runSpeed = options.runSpeed || CharacterController.RUN_SPEED;

    // Physics configuration
    this.gravity = options.gravity || CharacterController.GRAVITY;
    this.jumpVelocity = options.jumpVelocity || CharacterController.JUMP_VELOCITY;

    // Whether this controller is active
    this.enabled = true;

    // When true, input is suppressed but physics (gravity, collision) still run.
    // Used during NPC dialog so jump arcs complete naturally.
    this.dialogPaused = false;
  }

  /**
   * Read input and update input direction
   */
  readInput() {
    if (!this.inputManager || !this.enabled) return;

    const keys = this.inputManager.keys;

    // Reset input
    let inputX = 0;
    let inputZ = 0;

    // WASD mapped to screen directions:
    // W = up on screen = forward in view
    // S = down on screen = backward in view
    // A = left on screen
    // D = right on screen
    if (keys.w || keys.W) inputZ = 1;   // Forward
    if (keys.s || keys.S) inputZ = -1;  // Backward
    if (keys.a || keys.A) inputX = -1;  // Left
    if (keys.d || keys.D) inputX = 1;   // Right

    // Diagonal normalization
    if (inputX !== 0 && inputZ !== 0) {
      const length = Math.sqrt(inputX * inputX + inputZ * inputZ);
      inputX /= length;
      inputZ /= length;
    }

    this.inputDirection.set(inputX, 0, inputZ);

    // Check for run (shift key)
    this.isRunning = keys.Shift || keys.shift;

    // Check for jump (spacebar) - only trigger on initial press, not hold
    const jumpKeyDown = keys[' '];
    this.jumpPressed = jumpKeyDown && !this.jumpHeld;
    this.jumpHeld = jumpKeyDown;

    // Attack keys (E, R, F) are handled directly via keydown in main.js
    // (same path as joystick/click attacks) to avoid issues with leaked
    // capture-phase listeners or missed Character.update() frames.
    // attackPressed is set by the keydown handler in main.js.
  }

  /**
   * Convert screen-space input to world-space direction
   * @returns {Vector3} World-space movement direction
   */
  getWorldDirection() {
    if (this.inputDirection.lengthSquared() < 0.001) {
      return new Vector3(0, 0, 0);
    }

    // Get camera rotation
    const rotation = Transform.getCameraRotation(this.camera);
    const cos = rotation.cos;
    const sin = rotation.sin;

    // Rotate input by camera angle to get world direction
    // Input X = screen right, Input Z = screen up (into screen)
    // When camera rotates right (+angle), screen "up" rotates left in world space
    const worldX = this.inputDirection.x * cos - this.inputDirection.z * sin;
    const worldZ = this.inputDirection.x * sin + this.inputDirection.z * cos;

    return new Vector3(worldX, 0, worldZ).normalize();
  }

  /**
   * Update the character's facing angle based on movement
   * @param {Vector3} worldDirection - Movement direction in world space
   */
  updateFacing(worldDirection) {
    if (worldDirection.lengthSquared() > 0.001) {
      // Calculate facing angle from movement direction
      const angle = DirectionResolver.angleFromMovement(worldDirection.x, worldDirection.z);
      if (angle !== null) {
        this.character.setFacing(angle);
      }
    }
  }

  /**
   * Update the character (called each frame)
   * @param {number} deltaTime - Time since last frame in seconds
   */
  update(deltaTime) {
    if (!this.enabled) return;

    // During dialog: suppress all input but keep physics running so jump arcs complete
    if (this.dialogPaused) {
      this.jumpedThisFrame = false;
      // Zero horizontal movement
      this.character.velocity.x = 0;
      this.character.velocity.z = 0;
      // Apply gravity if not grounded
      if (!this.character.isGrounded) {
        this.character.velocity.y -= this.gravity * deltaTime;
        if (this.character.velocity.y < -CharacterController.MAX_FALL_SPEED) {
          this.character.velocity.y = -CharacterController.MAX_FALL_SPEED;
        }
      }
      this.moveWithCollision(deltaTime);
      this.checkGround();
      this.updateAnimationState(new Vector3(0, 0, 0));
      return;
    }

    // Read input
    this.readInput();

    // Cancel path following if any movement input is detected
    if (this.character.pathFollower && this.character.pathFollower.isFollowing) {
      const hasMovementInput = this.inputDirection.lengthSquared() > 0.001;
      if (hasMovementInput) {
        this.character.pathFollower.stop(true); // true = cancelled by user
      }
    }

    // Handle jump input
    this.jumpedThisFrame = false;
    if (this.jumpPressed && this.character.isGrounded) {
      this.jump();
      // Trigger jump animation (use character's config)
      const jumpAnim = this.character.config?.jump || 'jumping-1';
      this.character.triggerAction(jumpAnim);
      this.jumpedThisFrame = true;
      if (this.onJump) this.onJump(this.character);
    }

    // Attack input (E, R, F) is handled by keydown handler in main.js.
    // attackPressed is set there and cleared after one frame.
    if (this.attackPressed) {
      this.attackPressed = null;
    }

    // Check if path following is active (PathFollower handles movement)
    const isPathFollowing = this.character.pathFollower && this.character.pathFollower.isFollowing;

    // Get world-space movement direction
    const worldDirection = this.getWorldDirection();

    // Only apply keyboard/joystick movement if NOT path following
    if (!isPathFollowing) {
      // Stamina-based sprinting: drain while running + moving, force walk when empty
      // Buffer: once exhausted, must regen to 20% before sprinting again
      const stats = this.character.combatStats;
      if (stats) {
        if (this._sprintLocked && stats.stamina >= stats.maxStamina * 0.2) {
          this._sprintLocked = false;
        }
        if (this._sprintLocked && this.isRunning) {
          this.isRunning = false;
        }
        if (this.isRunning && worldDirection.lengthSquared() > 0.001) {
          const SPRINT_DRAIN = 15; // stamina per second
          stats.stamina -= SPRINT_DRAIN * deltaTime;
          if (stats.stamina <= 0) {
            stats.stamina = 0;
            this.isRunning = false;
            this._sprintLocked = true; // block sprint until buffer regens
          }
        }
      }

      // Calculate movement speed (with weight penalty when overweight)
      const weightMult = this.inventory?.getSpeedMultiplier() ?? 1.0;
      const speed = (this.isRunning ? this.runSpeed : this.walkSpeed) * weightMult;

      // Set horizontal velocity
      const horizontalVelocity = worldDirection.multiply(speed);
      this.character.velocity.x = horizontalVelocity.x;
      this.character.velocity.z = horizontalVelocity.z;
    }

    // Apply gravity if not grounded
    if (!this.character.isGrounded) {
      this.character.velocity.y -= this.gravity * deltaTime;
      // Clamp fall speed
      if (this.character.velocity.y < -CharacterController.MAX_FALL_SPEED) {
        this.character.velocity.y = -CharacterController.MAX_FALL_SPEED;
      }
    }

    // Move with collision
    this.moveWithCollision(deltaTime);

    // Check ground after movement
    this.checkGround();

    // Update facing direction (only from keyboard input, path follower sets its own facing)
    if (!isPathFollowing) {
      this.updateFacing(worldDirection);
    }

    // Update animation state
    this.updateAnimationState(worldDirection);
  }

  /**
   * Move the character with collision detection
   * @param {number} deltaTime - Time since last frame
   */
  moveWithCollision(deltaTime) {
    if (!this.collisionSystem) {
      // No collision system - just move directly
      this.character.x += this.character.velocity.x * deltaTime;
      this.character.y += this.character.velocity.y * deltaTime;
      this.character.z += this.character.velocity.z * deltaTime;
      return;
    }

    // Save old position BEFORE any movement for velocity reset checks
    const oldX = this.character.x;
    const oldY = this.character.y;
    const oldZ = this.character.z;

    // Use swept collision to prevent tunneling through floors
    const newPos = this.collisionSystem.moveWithSweptY(
      this.character,
      this.character.velocity,
      deltaTime
    );

    // Calculate intended positions for step-up check
    const intendedX = oldX + this.character.velocity.x * deltaTime;
    const intendedZ = oldZ + this.character.velocity.z * deltaTime;

    // Check for step-up if horizontal movement was blocked
    // Use fresh ground check instead of stale isGrounded state
    const horizontalBlocked = (Math.abs(newPos.x - intendedX) > 0.001 || Math.abs(newPos.z - intendedZ) > 0.001);
    const hasHorizontalInput = (this.character.velocity.x !== 0 || this.character.velocity.z !== 0);

    if (this.groundDetector && horizontalBlocked && hasHorizontalInput) {
      // Do a fresh ground check at current position for step-up eligibility
      const currentGroundCheck = this.groundDetector.checkGround(this.character);

      if (currentGroundCheck.onGround) {
        const stepY = this.groundDetector.tryStepUp(this.character, intendedX, intendedZ);
        if (stepY !== null && stepY > oldY) {
          // Verify the stepped position doesn't collide
          if (!this.collisionSystem.checkCollision(this.character, intendedX, stepY, intendedZ)) {
            newPos.y = stepY;
            newPos.x = intendedX;
            newPos.z = intendedZ;
          }
        }
      }
    }

    // Apply new position
    this.character.x = newPos.x;
    this.character.y = newPos.y;
    this.character.z = newPos.z;

    // Detect if vertical movement was constrained by comparing intended vs actual
    const intendedY = oldY + this.character.velocity.y * deltaTime;

    if (this.character.velocity.y < 0) {
      // Falling - if we didn't move down as much as intended, we hit ground
      if (newPos.y > intendedY + 0.001) {
        this.character.velocity.y = 0;
      }
    } else if (this.character.velocity.y > 0) {
      // Rising - if we didn't move up as much as intended, we hit ceiling
      if (newPos.y < intendedY - 0.001) {
        this.character.velocity.y = 0;
      }
    }
  }

  /**
   * Check if character is on ground and snap to ground level
   */
  checkGround() {
    if (!this.groundDetector) {
      this.character.isGrounded = true;
      return;
    }

    // Don't snap to ground when jumping up - let the character rise
    if (this.character.velocity.y > 0) {
      this.character.isGrounded = false;
      return;
    }

    const groundCheck = this.groundDetector.checkGround(this.character);
    this.character.isGrounded = groundCheck.onGround;

    if (groundCheck.onGround && groundCheck.groundY !== null) {
      // Snap to ground level - this handles both:
      // 1. Character slightly above ground (normal case)
      // 2. Character slightly embedded in ground (physics glitch)
      this.character.y = groundCheck.groundY;
      this.character.groundY = groundCheck.groundY;

      // Stop downward velocity when on ground
      if (this.character.velocity.y < 0) {
        this.character.velocity.y = 0;
      }
    } else if (!groundCheck.onGround && this.collisionSystem) {
      // Not on ground - but check if we're somehow stuck inside geometry
      // This can happen after physics glitches; try to push character up
      const collisions = this.collisionSystem.findCollisions(
        this.character,
        this.character.x,
        this.character.y,
        this.character.z
      );

      if (collisions.length > 0) {
        // Find highest collision surface and push character on top
        let highestTop = this.character.y;
        for (const cuboid of collisions) {
          const cuboidTop = cuboid.y + cuboid.height;
          if (cuboidTop > highestTop) {
            highestTop = cuboidTop;
          }
        }

        // Push character up to stand on top of blocking geometry
        if (highestTop > this.character.y) {
          this.character.y = highestTop;
          this.character.velocity.y = 0;
          this.character.isGrounded = true;
          this.character.groundY = highestTop;
        }
      }
    }
  }

  /**
   * Update animation state based on movement
   * @param {Vector3} worldDirection - Movement direction
   */
  updateAnimationState(worldDirection) {
    // Check if moving from keyboard/joystick OR from path following (check actual velocity)
    const isKeyboardMoving = worldDirection.lengthSquared() > 0.001;
    const isPathFollowing = this.character.pathFollower && this.character.pathFollower.isFollowing;
    const velocityMoving = (this.character.velocity.x * this.character.velocity.x +
                           this.character.velocity.z * this.character.velocity.z) > 0.01;
    const isMoving = isKeyboardMoving || (isPathFollowing && velocityMoving);

    const isGrounded = this.character.isGrounded;

    // Detect landing (transition from airborne to grounded)
    const justLanded = isGrounded && !this.wasGrounded;

    // Detect falling off edge (was grounded, now airborne, didn't jump)
    const fellOffEdge = !isGrounded && this.wasGrounded && !this.jumpedThisFrame;

    // Detect blocked jump (tried to jump but immediately blocked by ceiling)
    const jumpBlocked = this.jumpedThisFrame && isGrounded;

    // Track peak height during airtime (for drop-height-scaled landing sound)
    if (!isGrounded && this.wasGrounded) {
      // Just became airborne — record starting Y
      this._fallStartY = this.character.y;
    } else if (!isGrounded && this._fallStartY !== null) {
      // Update to peak Y (so jumps register the full arc height)
      if (this.character.y > this._fallStartY) {
        this._fallStartY = this.character.y;
      }
    }

    this.wasGrounded = isGrounded;

    // If fell off edge, trigger jump animation to show falling pose
    if (fellOffEdge) {
      const jumpAnim = this.character.config?.jump || 'jumping-1';
      this.character.triggerAction(jumpAnim);
    }

    // If we just landed or jump was blocked, force animation change
    if (justLanded) {
      // Compute how far we fell (drop height below the takeoff point)
      let dropHeight = 0;
      if (this._fallStartY !== null) {
        dropHeight = Math.max(0, this._fallStartY - this.character.y);
        this._fallStartY = null;
      }
      if (this.onLand) this.onLand(this.character, dropHeight);
      // Clear saved jump state — no longer mid-jump
      if (this.character.animator) {
        this.character.animator._savedJumpState = null;
      }
    }
    if (justLanded || jumpBlocked) {
      this.character.setMovementState(isMoving, this.isRunning, true);
    } else if (isGrounded) {
      // On ground, let movement state control animation normally
      this.character.setMovementState(isMoving, this.isRunning);
    }
    // While airborne, keep the jump animation on its last frame (falling pose)
  }

  /**
   * Make the character jump
   */
  jump() {
    if (this.character.isGrounded) {
      this.character.velocity.y = this.jumpVelocity;
      this.character.isGrounded = false;
    }
  }

  /**
   * Enable the controller
   */
  enable() {
    this.enabled = true;
  }

  /**
   * Disable the controller
   */
  disable() {
    this.enabled = false;
    // Reset velocity when disabled
    this.character.velocity.set(0, 0, 0);
  }
}
